import { Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filters } from '../../implementation-specialist/models/reportsFilters';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import {clearReportInfo, setReportFilters} from '../state/users/admin-slice';
import { getAllv2Reports, handleGetFilteredReports } from '../state/users/admin-thunk';
import AdminReports from './admin-users/admin-reports-filter';
import QuickSightDashboard from './reports-qs';
import { showErrorStatus } from '../../security/state/user-slice';
import { reportingErrors } from "../../shared/enums";
import {
  useSearchParams,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import _ from 'lodash';

interface ReportsProps {
  page: "admin" | "client-owner";
}

function ReportsView(props: ReportsProps) {

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams();
  const params = useParams<{ reportId?: string }>();
  const [reportId, setReportId] = useState<number | null>(null);
  const [dashboardList, setDashboardList] = useState<any[]>([]);;
  const [clientId, setClientId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [forceKey, setForceKey] = useState(0)
  const clients = useSelector(
    (state: RootState) =>
        state.implementationContext.implementationSpecialistClient
            .filteredByIsActive
  );
  const reportFilters = useSelector(
      (state: RootState) => state.adminContext.adminUserContext.reportFilters
  )

  const clientList = useMemo(() => clients.map((client) => ({
    clientId: client.clientId,
    label: client.clientName
  })), [clients]);

  const handleDashboardChange = (newDashboardId: number ) => {
    setReportId(newDashboardId);
    navigate(`/operations/reports/${newDashboardId}${location.search}`, { replace: true });
  };

  const handleClientIdChange = (clientId: number | null ) => {
    setClientId(clientId);
    if(typeof clientId === 'number'){
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('clientId', clientId.toString());
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  };

  const handleDateChange = (startDt: any, endDt: any) => {

    setForceKey(prev => prev +1)
    const searchParams = new URLSearchParams(location.search);

    if(startDt){
      const formattedStartDate = startDt.format("YYYY-MM-DDTHH:mm:ss");
      setStartDate(formattedStartDate);
      searchParams.set('startDate', formattedStartDate);
    }else{
      setStartDate(startDt);
      searchParams.delete('startDate')
    }
    if(endDt){
      const formattedEndDate = endDt.format("YYYY-MM-DDTHH:mm:ss");
      setEndDate(formattedEndDate);
      searchParams.set('endDate', formattedEndDate);
    }else{
      setEndDate(endDt);
      searchParams.delete('endDate')
    }
    navigate({ search: searchParams.toString()}, { replace: true });
  };

  useEffect(() => {
    const fetchReports = async () => {
        try {
            const { payload } = await dispatch(getAllv2Reports());
            if (payload && payload.length > 0) {
              let sortedReports = _.sortBy(payload, 'title')
              setDashboardList(sortedReports);
              return sortedReports?.[0]?.reportId
            } else {
                dispatch(showErrorStatus(reportingErrors.no_reports));
            }
        } catch (error) {
            dispatch(showErrorStatus(reportingErrors.fail));
        }
    };

    if(clientList.length > 0){
      fetchReports()
      .then(returnedReportId => setStateAndUrl(returnedReportId))
    }
  }, [clientList, searchParams, navigate, dispatch]);

  const setStateAndUrl = (returnedReportId: number)=>{

    const reportIdParam = parseInt(params.reportId || "") || returnedReportId;
    const clientIdParam = searchParams.get('clientId') || clientList?.[0]?.clientId;
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate')

    let url: string = `/operations/reports`
    const searchParamsToAdd = new URLSearchParams();
    let reportFilterObj: Record<string, any> = {};


    if(reportIdParam){
      setReportId(reportIdParam);
      url += `/${reportIdParam}`
    }
    if(clientIdParam) {
      setClientId(+clientIdParam);
      searchParamsToAdd.set('clientId', clientIdParam.toString())
      reportFilterObj.clientId = +clientIdParam
    }
    if(startDateParam) {
      setStartDate(decodeURIComponent(startDateParam));
      searchParamsToAdd.set('startDate', startDateParam)
      reportFilterObj.startDate = decodeURIComponent(startDateParam)
    }
    if(endDateParam) {
      setEndDate(decodeURIComponent(endDateParam));
      searchParamsToAdd.set('endDate', endDateParam)
      reportFilterObj.endDate = decodeURIComponent(endDateParam)
    }

    if(Object.keys(reportFilterObj).length >0){
      dispatch(setReportFilters({
        ...reportFilters,
        ...reportFilterObj
      }))
    }

    let queryString = searchParamsToAdd.toString();

    navigate(queryString ? `${url}?${queryString}` : url, { replace: true });

  }

  useEffect(() => {
    dispatch(clearReportInfo())
  }, [dispatch])

  function handleFilterReportType(reportsFilters: Filters) {
    dispatch(handleGetFilteredReports(reportsFilters));
  }

  return (
      <main>
          <div className="content fullwidth">
              <Grid container spacing={2}>
                  <Grid
                      item
                      sx={{
                          flex: {xs: '100%', md: '2.4 1 0%'},
                          width: {xs: '100%', md: 'auto'},
                      }}
                  >
                      {clientId && reportId && (
                          <AdminReports
                              handleFilterReportType={handleFilterReportType}
                              dashboardList={dashboardList}
                              reportId={reportId!}
                              clientId={clientId}
                              handleDashboardChange={handleDashboardChange}
                              handleClientIdChange={handleClientIdChange}
                              handleDateChange={handleDateChange}
                              clientList={clientList}
                          />
                      )}
                  </Grid>
                  <Grid
                      item
                      sx={{
                          flex: {xs: '100%', md: '9.6 1 0%'},
                          width: {xs: '100%', md: 'auto'},
                          paddingLeft: {md: '4px !important'},
                      }}
                  >
                      {clientId && reportId && (
                          <QuickSightDashboard
                              //Adding key to force a rerender of the component so it can refetch the report even if none of the inputs have changed
                              key={`${clientId}-${startDate}-${reportId}-${forceKey}`}
                              reportId={reportId!}
                              clientId={clientId}
                              startDate={startDate}
                              endDate={endDate}
                          />
                      )}
                  </Grid>
              </Grid>
          </div>
      </main>
  );
}

export default ReportsView;
